import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import toast from 'react-hot-toast'
import { eventsService } from '../services/eventsService'
import LoadingSpinner from '../components/common/LoadingSpinner'

const Events = () => {
  const { user } = useAuth()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [aiSuggestions, setAiSuggestions] = useState([])
  const [showAiSuggestions, setShowAiSuggestions] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [loadingAi, setLoadingAi] = useState(false)
  const [activeTab, setActiveTab] = useState('all') // all, today, upcoming, past
  
  const [showForm, setShowForm] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    time: '',
    type: 'other',
    description: '',
    priority: 'medium',
    duration: '1 hour'
  })

  useEffect(() => {
    loadEvents()
  }, [])

  // Helper function to get event status
  const getEventStatus = (event) => {
    const eventDateTime = new Date(`${event.date}T${event.time}`)
    const now = new Date()
    
    if (eventDateTime < now) {
      return { 
        status: 'past', 
        color: 'bg-gray-100 text-gray-600 border-gray-300', 
        label: '✓ Completed',
        sortOrder: 3
      }
    }
    
    const hoursUntil = (eventDateTime - now) / 3600000
    if (hoursUntil < 2) {
      return { 
        status: 'urgent', 
        color: 'bg-red-100 text-red-800 border-red-400', 
        label: '🚨 Very Soon',
        sortOrder: 0
      }
    }
    if (hoursUntil < 24) {
      return { 
        status: 'today', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-400', 
        label: '⚡ Today',
        sortOrder: 1
      }
    }
    if (hoursUntil < 48) {
      return { 
        status: 'tomorrow', 
        color: 'bg-blue-100 text-blue-800 border-blue-400', 
        label: '📅 Tomorrow',
        sortOrder: 2
      }
    }
    return { 
      status: 'upcoming', 
      color: 'bg-green-100 text-green-800 border-green-400', 
      label: '📆 Upcoming',
      sortOrder: 2
    }
  }

  // Helper function to get time until event
  const getTimeUntil = (event) => {
    const eventDateTime = new Date(`${event.date}T${event.time}`)
    const now = new Date()
    const diffMs = eventDateTime - now
    
    if (diffMs < 0) {
      const pastMs = Math.abs(diffMs)
      const pastDays = Math.floor(pastMs / 86400000)
      if (pastDays > 0) return `${pastDays} day${pastDays > 1 ? 's' : ''} ago`
      return 'Earlier today'
    }
    
    const minutes = Math.floor(diffMs / 60000)
    const hours = Math.floor(diffMs / 3600000)
    const days = Math.floor(diffMs / 86400000)
    
    if (minutes < 60) return `in ${minutes} min${minutes !== 1 ? 's' : ''}`
    if (hours < 24) return `in ${hours} hour${hours !== 1 ? 's' : ''}`
    if (days < 7) return `in ${days} day${days !== 1 ? 's' : ''}`
    const weeks = Math.floor(days / 7)
    return `in ${weeks} week${weeks !== 1 ? 's' : ''}`
  }

  const loadEvents = async () => {
    try {
      setLoading(true)
      console.log('📅 Loading events...')
      const response = await eventsService.getEvents()
      console.log('📋 Events API response:', response)
      
      // The API returns {success: true, data: [...]}
      const eventsData = response.data || []
      console.log('📅 Events data:', eventsData)
      console.log('📊 Number of events:', eventsData.length)
      
      setEvents(eventsData)
    } catch (error) {
      console.error('❌ Events loading error:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const eventTypes = [
    { value: 'health', label: '🏥 Health', color: 'bg-green-100 text-green-800' },
    { value: 'work', label: '💼 Work', color: 'bg-blue-100 text-blue-800' },
    { value: 'personal', label: '🏠 Personal', color: 'bg-purple-100 text-purple-800' },
    { value: 'social', label: '👥 Social', color: 'bg-pink-100 text-pink-800' },
    { value: 'other', label: '📅 Other', color: 'bg-gray-100 text-gray-800' }
  ]

  const handleInputChange = (e) => {
    setNewEvent({
      ...newEvent,
      [e.target.name]: e.target.value
    })
  }

    const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!newEvent.title || !newEvent.date || !newEvent.time) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      console.log('📝 Submitting event:', newEvent)
      const eventData = {
        ...newEvent,
        title: newEvent.title.trim(),
        description: newEvent.description.trim()
      }
      console.log('📤 Sending event data:', eventData)

      let response
      if (editingEvent) {
        // Update existing event
        response = await eventsService.updateEvent(editingEvent._id, eventData)
        console.log('✅ Event update response:', response)
        
        const updatedEvent = response.data
        setEvents(prev => prev.map(evt => 
          evt._id === editingEvent._id ? updatedEvent : evt
        ).sort((a, b) => 
          new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time)
        ))
        
        toast.success('Event updated successfully!')
      } else {
        // Create new event
        response = await eventsService.createEvent(eventData)
        console.log('✅ Event creation response:', response)
        
        const createdEvent = response.data
        setEvents(prev => [...prev, createdEvent].sort((a, b) => 
          new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time)
        ))
        
        toast.success('Event added successfully!')
      }
      
      setNewEvent({
        title: '',
        date: '',
        time: '',
        type: 'other',
        description: '',
        priority: 'medium',
        duration: '1 hour'
      })
      
      setEditingEvent(null)
      setShowForm(false)
    } catch (error) {
      console.error('❌ Event submission error:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error(editingEvent ? 'Failed to update event' : 'Failed to create event')
    }
  }

  const handleEditEvent = (event) => {
    setEditingEvent(event)
    setNewEvent({
      title: event.title,
      date: event.date,
      time: event.time,
      type: event.type,
      description: event.description || '',
      priority: event.priority || 'medium',
      duration: event.duration || '1 hour'
    })
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return
    }

    try {
      await eventsService.deleteEvent(eventId)
      setEvents(prev => prev.filter(evt => evt._id !== eventId))
      toast.success('Event deleted successfully!')
    } catch (error) {
      console.error('❌ Event deletion error:', error)
      toast.error('Failed to delete event')
    }
  }

  const handleCancelEdit = () => {
    setEditingEvent(null)
    setNewEvent({
      title: '',
      date: '',
      time: '',
      type: 'other',
      description: '',
      priority: 'medium',
      duration: '1 hour'
    })
    setShowForm(false)
  }

  const getAiSuggestions = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter what kind of events you need')
      return
    }

    try {
      setLoadingAi(true)
      console.log('🤖 Requesting AI suggestions for:', aiPrompt)
      const response = await eventsService.getEventSuggestions(aiPrompt, `User context: ${user?.name || 'User'}`)
      console.log('🤖 AI suggestions response:', response)
      
      // The API returns {success: true, data: {suggestions: [...], rawResponse: "..."}}
      const suggestions = response.data?.suggestions || []
      console.log('✨ AI suggestions:', suggestions)
      console.log('📊 Number of suggestions:', suggestions.length)
      
      setAiSuggestions(suggestions)
      setShowAiSuggestions(true)
      toast.success(`Generated ${suggestions.length} AI suggestions!`)
    } catch (error) {
      console.error('❌ AI suggestions error:', error)
      console.error('❌ Error details:', error.response?.data)
      toast.error('Failed to get AI suggestions')
    } finally {
      setLoadingAi(false)
    }
  }

  const addSuggestedEvent = async (suggestion) => {
    try {
      const eventData = {
        title: suggestion.title,
        date: suggestion.suggestedDate || new Date().toISOString().split('T')[0],
        time: '09:00', // Default time
        type: suggestion.type || 'other',
        description: suggestion.description || '',
        priority: suggestion.priority || 'medium',
        duration: suggestion.duration || '1 hour',
        aiGenerated: true
      }

      const response = await eventsService.createEvent(eventData)
      const createdEvent = response.data || response
      
      setEvents(prev => [...prev, createdEvent].sort((a, b) => 
        new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time)
      ))
      
      toast.success('AI suggested event added!')
    } catch (error) {
      console.error('Error adding suggested event:', error)
      toast.error('Failed to add suggested event')
    }
  }

  const getEventTypeInfo = (type) => {
    return eventTypes.find(t => t.value === type) || eventTypes[eventTypes.length - 1]
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const today = new Date()
    const diffTime = date - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays === -1) return 'Yesterday'
    if (diffDays > 1) return `In ${diffDays} days`
    if (diffDays < -1) return `${Math.abs(diffDays)} days ago`
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Events & Schedule 📅
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Manage your upcoming events and appointments
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm sm:text-base w-full sm:w-auto"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Event
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* AI Event Suggestions */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6 mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-2xl">🤖</span>
            <h2 className="text-lg font-semibold text-gray-900">AI Event Suggestions</h2>
          </div>
          
          <div className="flex space-x-4 mb-4">
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="What events do you need? e.g., 'health checkups', 'team meetings', 'family time'"
              className="flex-1 input-field"
              onKeyPress={(e) => e.key === 'Enter' && getAiSuggestions()}
            />
            <button
              onClick={getAiSuggestions}
              disabled={loadingAi}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors duration-200"
            >
              {loadingAi ? '🔄' : '✨'} Suggest
            </button>
          </div>

          {showAiSuggestions && aiSuggestions.length > 0 && (
            <div className="grid gap-3">
              <h3 className="text-sm font-medium text-gray-700">AI Generated Suggestions:</h3>
              {aiSuggestions.map((suggestion, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{suggestion.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>📅 {suggestion.suggestedDate || 'Flexible'}</span>
                        <span>⏱️ {suggestion.duration || '1 hour'}</span>
                        <span className={`px-2 py-1 rounded ${getEventTypeInfo(suggestion.type).color}`}>
                          {getEventTypeInfo(suggestion.type).label}
                        </span>
                        <span className={`px-2 py-1 rounded ${
                          suggestion.priority === 'high' ? 'bg-red-100 text-red-800' :
                          suggestion.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {suggestion.priority || 'medium'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => addSuggestedEvent(suggestion)}
                      className="ml-4 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors duration-200 text-sm"
                    >
                      ➕ Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Event Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingEvent ? '✏️ Edit Event' : '➕ Add New Event'}
              </h2>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={newEvent.title}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter event title"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    name="type"
                    value={newEvent.type}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    {eventTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={newEvent.date}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time *
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={newEvent.time}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={newEvent.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="input-field"
                  placeholder="Enter event description (optional)"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingEvent ? '💾 Update Event' : '➕ Add Event'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Events List */}
        <div>
          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px overflow-x-auto">
                {[
                  { id: 'all', label: 'All Events', icon: '📋' },
                  { id: 'upcoming', label: 'Upcoming', icon: '🔜' },
                  { id: 'today', label: 'Today', icon: '📌' },
                  { id: 'past', label: 'Past', icon: '✅' }
                ].map(tab => {
                  const count = events.filter(event => {
                    if (tab.id === 'all') return true
                    const status = getEventStatus(event).status
                    if (tab.id === 'upcoming') return status === 'upcoming' || status === 'tomorrow'
                    if (tab.id === 'today') return status === 'today' || status === 'urgent'
                    if (tab.id === 'past') return status === 'past'
                    return false
                  }).length
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-3 px-4 sm:px-6 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.icon} {tab.label} <span className="ml-1 text-xs opacity-75">({count})</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Filtered Events */}
          <div className="space-y-3 sm:space-y-4">
            {(() => {
              const filteredEvents = events.filter(event => {
                if (activeTab === 'all') return true
                const status = getEventStatus(event).status
                if (activeTab === 'upcoming') return status === 'upcoming' || status === 'tomorrow'
                if (activeTab === 'today') return status === 'today' || status === 'urgent'
                if (activeTab === 'past') return status === 'past'
                return false
              })

              if (filteredEvents.length === 0) {
                return (
                  <div className="text-center py-8 sm:py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                    <div className="text-4xl sm:text-6xl mb-4">
                      {activeTab === 'today' && '�'}
                      {activeTab === 'upcoming' && '🔜'}
                      {activeTab === 'past' && '✅'}
                      {activeTab === 'all' && '�📅'}
                    </div>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                      No {activeTab !== 'all' ? activeTab : ''} events
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 px-4">
                      {activeTab === 'all' ? 'Add your first event to get started' : `You have no ${activeTab} events`}
                    </p>
                    {activeTab === 'all' && (
                      <button
                        onClick={() => setShowForm(true)}
                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm sm:text-base"
                      >
                        Add Event
                      </button>
                    )}
                  </div>
                )
              }

              return filteredEvents.map((event) => {
                const typeInfo = getEventTypeInfo(event.type)
                const statusInfo = getEventStatus(event)
                const timeUntil = getTimeUntil(event)
                
                return (
                  <div key={event.id} className={`bg-white rounded-xl shadow-sm border-l-4 p-4 sm:p-6 ${statusInfo.color.replace('bg-', 'border-l-').split(' ')[0]}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3 mb-2">
                          {/* Status Badge */}
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.color} whitespace-nowrap`}>
                            {statusInfo.label}
                          </span>
                          
                          {/* Priority Badge */}
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            event.priority === 'high' ? 'bg-red-100 text-red-800' :
                            event.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {event.priority === 'high' && '🔥'} 
                            {event.priority === 'medium' && '⚡'} 
                            {event.priority === 'low' && '💚'} 
                            {event.priority}
                          </span>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                            {event.title}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.color} w-fit`}>
                            {typeInfo.label}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {formatDate(event.date)}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {new Date(`2000-01-01 ${event.time}`).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                          <div className="flex items-center font-semibold">
                            ⏰ {timeUntil}
                          </div>
                        </div>
                        
                        {event.description && (
                          <p className="text-sm text-gray-700 mt-2">
                            {event.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex sm:flex-col gap-2 sm:ml-4">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                          title="Edit event"
                        >
                          <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event._id)}
                          className="flex items-center justify-center px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors duration-200"
                          title="Delete event"
                        >
                          <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Events
